import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserDoc, getFamilyDoc, getFamilyByInviteToken, acceptInvite } from '../../services/firebase';

const AgeGate = () => {
  const { authUser, userDoc } = useAuth();
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Once done and familyId has propagated, navigate to wallet automatically
  useEffect(() => {
    if (done && userDoc?.familyId) {
      sessionStorage.removeItem('inviteJustAccepted');
      window.location.replace('/');
    }
  }, [done, userDoc?.familyId]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      // 1. Confirm age
      await updateUserDoc(authUser.uid, { ageConfirmed: true });

      // 2. Show success screen immediately before auth context re-renders routing
      setDone(true);

      // 3. If there's a pending invite token, accept it now in the background
      const pendingToken = sessionStorage.getItem('pendingInviteToken');
      if (pendingToken) {
        try {
          const parts = pendingToken.split('/');
          let family = null;
          if (parts.length === 2) {
            family = await getFamilyDoc(parts[0]);
            if (family?.inviteToken !== parts[1]) family = null;
          } else {
            family = await getFamilyByInviteToken(pendingToken);
          }
          if (family) {
            await acceptInvite(family.id, authUser.uid);
            sessionStorage.removeItem('pendingInviteToken');
            sessionStorage.setItem('inviteJustAccepted', '1');
          }
        } catch (err) {
          console.error('Auto-accept failed:', err);
        }
      }
    } catch (err) {
      console.error(err);
      setDone(false);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="screen screen--centered screen--no-nav">
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 72, marginBottom: 16 }}>🎉</div>
          <h1 style={{ fontSize: '1.75rem', marginBottom: 12 }}>You're all set!</h1>
          <p style={{ color: 'var(--color-text-2)', lineHeight: 1.6, marginBottom: 32 }}>
            Your wallet is connected and ready to go. Log your spending, plan ahead, and save what's left each week.
          </p>
          {userDoc?.familyId ? (
            <a href="/" className="btn btn--primary btn--full btn--lg"
              style={{ display: 'block', textAlign: 'center' }}>
              Open my wallet →
            </a>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              color: 'var(--color-text-3)', fontSize: '0.875rem' }}>
              <div className="spinner" />
              Setting up your wallet...
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="screen screen--centered screen--no-nav">
      <div style={{ width: '100%', maxWidth: 380, textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 24 }}>🎓</div>
        <h1 style={{ fontSize: '1.75rem', marginBottom: 12 }}>One quick thing</h1>
        <p style={{ color: 'var(--color-text-2)', lineHeight: 1.6, marginBottom: 40 }}>
          This app is designed for students who are 16 or older.
          Please confirm your age to continue.
        </p>

        <div
          className="card"
          style={{ textAlign: 'left', marginBottom: 32, cursor: 'pointer' }}
          onClick={() => setConfirmed(!confirmed)}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{
              width: 22, height: 22, borderRadius: 6, border: '2px solid',
              borderColor: confirmed ? 'var(--color-primary)' : 'var(--color-border)',
              background: confirmed ? 'var(--color-primary)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginTop: 2, transition: 'all 150ms ease',
            }}>
              {confirmed && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <p style={{ fontSize: '0.9375rem', lineHeight: 1.5, color: 'var(--color-text)' }}>
              I confirm that I am 16 years of age or older
            </p>
          </div>
        </div>

        <button
          className="btn btn--primary btn--full btn--lg"
          onClick={handleConfirm}
          disabled={!confirmed || loading}
        >
          {loading
            ? <span className="spinner spinner--white" style={{ width: 20, height: 20 }} />
            : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default AgeGate;
